import { DateTime } from 'luxon';

import { T } from './translation';

export const isStaffPick = (badge: string) =>
  [
    'staffpick',
    'staffpick-premiere',
    'staffpick-best-of-the-year',
    'staffpick-best-of-the-month',
  ].includes(badge);

/**
 * Get resource id number from resource uri
 * @example
 * idFromUri('/videos/1234') // returns 1234
 * idFromUri('/comments/2442/reply/987') // returns 987
 * idFromUri('/users/159333782') // returns 159333782
 */
export const idFromUri = (uri: string | undefined | null) => {
  return uri ? parseInt(uri.substring(uri.lastIndexOf('/') + 1), 10) : 0;
};

/**
 * Parse "/video/:id" from the url to "?video=:id"
 * @param options.eventId - Event id to be appended to the query to override it as event
 * @param options.baseUrl - Base url to be used to override the link base url
 * This should be used only for GCTL data `link`:
 * - In pages, there might be other search query from the page level
 * - In GCTL uri, the clipHash is append with `:` not `/`
 * @example contextUrlParser('/video/1234') // returns { clipId: '1234', clipRequestId: '1234', href: '?video=1234' }
 * @example contextUrlParser('/showcase/6789/video/1234') // returns { clipId: '1234', clipRequestId: '1234', href: '/showcase/6789?video=1234' }
 * @example contextUrlParser('/showcase/6789/video/1234/ab12') // returns { clipId: '1234', clipRequestId: '1234:ab12', href: '/showcase/6789?video=1234' }
 * @example contextUrlParser('/showcase/6789/video/1234/ab12', { eventId: '1023' }) // returns { clipId: '1234', clipRequestId: '1234:ab12', href: '/showcase/6789?event=1023' }
 * @example contextUrlParser('/showcase/6789/video/1234/ab12', { eventId: '1023', baseUrl: 'https://vimeo.com/showcase/6789/embed2' }) // returns { clipId: '1234', clipRequestId: '1234:ab12', href: 'https://vimeo.com/showcase/6789/embed2?event=1023' }
 */
export const contextUrlParser = (url: string, options?: { eventId?: string; baseUrl?: string }) => {
  const baseUrl = options?.baseUrl || url.split('/video/')[0];

  const match = url.match(/\/video\/(\d+)(\/\w+)?/);
  const clipId = match?.[1] ?? '';
  const clipRequestId = `${clipId}${match?.[2] ?? ''}`.replace('/', ':');

  const query = options?.eventId ? `event=${options.eventId}&video=${clipId}` : `video=${clipId}`;

  const link = clipId ? `${baseUrl}?${query}` : baseUrl;

  return { clipId, clipRequestId, link };
};

export const formatTimeBySourceType = ({
  isScreenRecord,
  isVimeoCreate,
  isLiveEvent,
  videoCreatedTime,
}: {
  isScreenRecord: boolean;
  isVimeoCreate: boolean;
  isLiveEvent: boolean;
  videoCreatedTime: Date;
}) => {
  let onCreatedTranslationFn, createdOnString;

  if (isVimeoCreate) {
    onCreatedTranslationFn = T.createdOn;
  } else if (isScreenRecord) {
    onCreatedTranslationFn = T.recordedOn;
  } else if (isLiveEvent) {
    onCreatedTranslationFn = T.streamedOn;
  } else {
    onCreatedTranslationFn = T.uploadedOn;
  }

  const locale = navigator.language || 'en-US';
  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
  };

  const dateFormatter = new Intl.DateTimeFormat(locale, dateOptions);
  const timeFormatter = new Intl.DateTimeFormat(locale, timeOptions);
  const videoDate = dateFormatter.format(videoCreatedTime);
  const videoTime = timeFormatter.format(videoCreatedTime).toLowerCase();

  createdOnString = onCreatedTranslationFn({ videoDate, videoTime });

  return { createdOnString };
};

export const findWithAdjacent = <T>(clips: Array<T>, predicate: (clip: T) => boolean) => {
  const index = clips.findIndex(predicate);

  /** When no item found, all should be undefined */
  if (index === -1) {
    return { selected: undefined, prev: undefined, next: undefined };
  }

  /** First item in the list, or the found item is the only item in the list, doesn't have prev item */
  const prev = index === 0 ? undefined : clips[index - 1];

  /** Last item in the list, or the found item is the only item in the list, doesn't have next item */
  const next = index === clips.length - 1 ? undefined : clips[index + 1];
  return { selected: clips[index], prev, next };
};

/**
 * Returns the pathname from the current URL
 */
export const usePathname = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.location.pathname;
};

/**
 * Find the next non-live or archived live clip in the list of clips
 * @example
 * findNextPlayableClip('1234', clips);
 */
export const findNextPlayableClip = <T extends { clipId: string; eventId?: string }>(
  clipId: string,
  clips?: Array<T>,
): T | undefined => {
  const nextClip = clips
    ? findWithAdjacent(clips, (clip) => clip.clipId === clipId)?.next
    : undefined;
  if (nextClip?.eventId) {
    return findNextPlayableClip(nextClip.clipId, clips); // Recursively find the next playable clip
  }

  return nextClip;
};

/**
 * Converts a date string in ISO format to a DateTime object.
 * @example fromISO('2021-08-25T12:00:00.000Z') //=> DateTime { ts: 1629892800000, ... }
 */
export const fromISO = (dateString: string = '') => {
  const locale = navigator.language || 'en-US';
  return DateTime.fromISO(dateString, { locale });
};
